import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  AjusteDeParaContextData,
  AjusteDeParaContextProvider,
} from '../types/context';
import {
  entradaProps,
  formType,
  prodTableProps,
  saidaProps,
  selectProps,
  tipoAjusteProps,
} from '../types';
import { defaultProdTable, defaultForm } from '../defaultData';
import { LojaContext } from '~/context/loja';
import { getInfos } from '../services/getInfos';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from '../validations';
import { ajusteDeParaServices } from '../services';

const MySwal = withReactContent(Swal);
export const AjusteDeParaContext = createContext({} as AjusteDeParaContextData);

export const AjusteDeParaProvider: React.FC<AjusteDeParaContextProvider> = ({
  children,
}) => {
  const { loja } = useContext(LojaContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [selectedLoja, setSelectedLoja] = useState<number>(loja.cod_loja);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [manterDados, setManterDados] = useState<boolean>(false);
  const [prodTable, setProdTable] = useState<prodTableProps>(defaultProdTable);
  const [tipoAjusteSaida, setTipoAjusteSaida] = useState<selectProps[]>([]);
  const [tipoAjusteEntrada, setTipoAjusteEntrada] = useState<selectProps[]>([]);
  const [prodDerivado, setProdDerivado] = useState<selectProps[]>([]);
  const [flgCancelado, setFlgCancelado] = useState<boolean>(false);

  const { getTiposDeAjuste } = getInfos;
  const { createAjusteDePara, deleteAjusteDePara } = ajusteDeParaServices;

  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });
  const formTela: formType = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  };

  useEffect(() => {
    (async () => {
      const { success, data } = await getTiposDeAjuste();
      if (success) {
        const optionsSaida = data.rowsSaida.map((i: tipoAjusteProps) => {
          return {
            value: i.cod_tipo_ajuste,
            label: `${i.cod_tipo_ajuste} - ${i.des_tipo_operacao} ${i.des_ajuste}`,
          };
        });
        const optionsEntrada = data.rowsEntrada.map((i: tipoAjusteProps) => {
          return {
            value: i.cod_tipo_ajuste,
            label: `${i.cod_tipo_ajuste} - ${i.des_tipo_operacao} ${i.des_ajuste}`,
          };
        });
        setTipoAjusteSaida(optionsSaida);
        setTipoAjusteEntrada(optionsEntrada);
      }
    })();
  }, [getTiposDeAjuste]);

  const handleManterDados = useCallback((val: boolean) => {
    setManterDados(val);
  }, []);

  const handleFocusInput = useCallback((campo: string) => {
    const input = document.querySelector(`[name="${campo}"]`) as HTMLElement;
    if (!input) return;
    setTimeout(() => {
      input?.click();
      input?.focus();
    }, 50);
  }, []);

  const onNew = () => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
  };

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      setLoading(true);
      const { row } = param;
      const {
        cod_ajuste_de_para,
        cod_loja,
        des_loja,
        // campos DE
        cod_produto_de,
        des_produto_de,
        cod_tipo_ajuste_de,
        tipo_operacao_view_de,
        val_peso_emb,
        qtd_ajuste_de,
        // campos PARA
        cod_produto_para,
        des_produto_para,
        cod_tipo_ajuste_para,
        tipo_operacao_view_para,
        qtd_ajuste_para,
        // outros
        flg_cancelado,
        qtd_est_atual,
        des_unidade_venda,
        des_unidade_compra,
      } = row;
      setFlgCancelado(flg_cancelado);
      setProdTable({
        cod_produto: cod_produto_de,
        des_produto: des_produto_de,
        qtd_est_atual,
        un_venda: des_unidade_venda,
        un_compra: des_unidade_compra,
        val_peso_emb,
      });
      reset({
        cod_ajuste_de_para,
        cod_loja: {
          label: des_loja,
          value: cod_loja,
        },
        busca_produto: {
          label: `${cod_produto_de} - ${des_produto_de}`,
          value: cod_produto_de,
        },
        cod_tipo_ajuste_origem: {
          label: `${cod_tipo_ajuste_de} - ${tipo_operacao_view_de}`,
          value: cod_tipo_ajuste_de,
        },
        qtd_ajuste: qtd_ajuste_de,
        qtd_derivado: qtd_ajuste_para,
        cod_produto_derivado: {
          label: `${cod_produto_para} - ${des_produto_para}`,
          value: cod_produto_para,
        },
        cod_tipo_ajuste_derivado: {
          label: `${cod_tipo_ajuste_para} - ${tipo_operacao_view_para}`,
          value: cod_tipo_ajuste_para,
        },
      });
      setSelectedLoja(cod_loja);
      setUpdate(true);
      setShowSearch(false);
      setLoading(false);
    },
    [reset],
  );

  const prepFomData = (data: any) => {
    const { label } = data.cod_produto_derivado;
    const codProdutoDerivado = label.split('-')[0].trim();
    const formData = {
      cod_loja: selectedLoja,
      saida: {
        cod_produto: prodTable.cod_produto,
        cod_tipo_ajuste: data.cod_tipo_ajuste_origem.value,
        num_documento: 'SAIDA PRODUCAO(DE-->PARA)',
        qtd_ajuste: Math.abs(data.qtd_ajuste),
        qtd_estoque_ant: prodTable.qtd_est_atual,
        qtd_estoque_post: prodTable.qtd_est_atual + Math.abs(data.qtd_ajuste),
      } as saidaProps,
      entrada: {
        cod_produto: codProdutoDerivado,
        cod_tipo_ajuste: data.cod_tipo_ajuste_derivado.value,
        num_documento: 'ENTRADA PRODUCAO(DE-->PARA)',
        qtd_ajuste: data.qtd_ajuste * prodTable.val_peso_emb,
        qtd_estoque_ant: 0,
        qtd_estoque_post:
          prodTable.qtd_est_atual + data.qtd_ajuste * prodTable.val_peso_emb,
      } as entradaProps,
    };
    return formData;
  };

  const onSave = handleSubmit(async (data) => {
    if (Number(data.qtd_ajuste) > 20) {
      const { isConfirmed } = await MySwal.fire({
        title: 'Quantidade de ajuste está correta?',
        text: 'Deseja prosseguir?',
        showCancelButton: true,
        showCloseButton: false,
        showConfirmButton: true,
        confirmButtonColor: '#0065FF',
        cancelButtonColor: '#DE350B',
        showDenyButton: false,
        confirmButtonText: 'SIM',
        cancelButtonText: 'NÃO',
        customClass: { popup: 'swal-wide' },
      });
      if (!isConfirmed) {
        return;
      }
    }
    if (prodTable.qtd_est_atual < 0) {
      const { isConfirmed } = await MySwal.fire({
        title: 'Produto Origem com estoque negativo.',
        text: ' Deseja prosseguir?',
        showCancelButton: true,
        showCloseButton: false,
        showConfirmButton: true,
        confirmButtonColor: '#0065FF',
        cancelButtonColor: '#DE350B',
        showDenyButton: false,
        confirmButtonText: 'SIM',
        cancelButtonText: 'NÃO',
        customClass: { popup: 'swal-wide' },
      });
      if (!isConfirmed) {
        return;
      }
    }
    setLoading(true);
    try {
      const { saida, entrada, cod_loja } = prepFomData(data);

      const { message, success } = await createAjusteDePara(
        saida,
        entrada,
        cod_loja,
      );
      if (success) {
        setShowSearch(false);
        toast.success(message);
        if (manterDados) {
          setValue('cod_produto_derivado', {
            value: undefined,
            label: undefined,
          });
          setValue('cod_tipo_ajuste_derivado', {
            value: undefined,
            label: undefined,
          });
          setValue('qtd_ajuste', '');
          return setValue('qtd_derivado', '');
        }
        resetFormData();
      }
    } catch (error: any) {
      toast.warn(`Erro ao gravar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  });

  const resetFormData = useCallback(async () => {
    setSelectedLoja(loja.cod_loja);
    reset({
      cod_loja: {
        value: loja.cod_loja,
        label: `${loja.des_loja} (${loja.des_cidade})`,
      },
      ...defaultForm,
    });
    setProdTable(defaultProdTable);
    handleManterDados(false);
    setFlgCancelado(false);
    setProdDerivado([]);
  }, [reset, loja, handleManterDados]);

  const handleDelete = useCallback(async () => {
    if (flgCancelado) return toast.warn('Ajuste já cancelado');
    const { success, message } = await deleteAjusteDePara(
      watch('cod_ajuste_de_para'),
    );
    if (success) {
      toast.success(message);
      setShowSearch(true);
      resetFormData();
    }
  }, [deleteAjusteDePara, resetFormData, watch, flgCancelado]);

  return (
    <AjusteDeParaContext.Provider
      value={{
        formTela,
        loading,
        setLoading,
        isUpdate,
        setUpdate,
        selectedLoja,
        showSearch,
        setShowSearch,
        prodTable,
        setProdTable,
        tipoAjusteSaida,
        tipoAjusteEntrada,
        prodDerivado,
        setProdDerivado,
        flgCancelado,
        setFlgCancelado,

        manterDados,
        handleManterDados,
        onNew,
        onRowClick,
        onSave,
        resetFormData,
        handleDelete,
        handleFocusInput,
      }}
    >
      {children}
    </AjusteDeParaContext.Provider>
  );
};
